import { useState } from "react";
import { ArrowRight, Check, Mail } from "lucide-react";
import { Reveal } from "./Motion";
import { getUtmContent } from "@/lib/utm";

const API = process.env.REACT_APP_BACKEND_URL;

export const TourCapture = ({ networkKey, accent = "#B4532A" }) => {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle"); // idle | sending | done | error

  const submit = async (e) => {
    e.preventDefault();
    setState("sending");
    try {
      const r = await fetch(`${API}/api/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, network: networkKey, content: getUtmContent(), origin: window.location.origin }),
      });
      setState(r.ok ? "done" : "error");
    } catch {
      setState("error");
    }
  };

  return (
    <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-24" data-testid="tour-capture-section">
      <Reveal>
        <div className="grid lg:grid-cols-[1fr_1.1fr] gap-8 lg:gap-14 items-center rounded-[2rem] border border-[#E8DDD3] bg-white p-8 sm:p-12">
          <div>
            <div className="font-mono text-xs uppercase tracking-[0.25em]" style={{ color: accent }}>Not ready yet?</div>
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl tracking-tight text-[#1F1A17]">We'll send you the <em>60-second tour.</em></h2>
            <p className="mt-4 text-sm leading-relaxed text-[#685F58]">
              One email with the tour and the kitchen-table checklist. No newsletter, no follow-up sequence. Just the thing you asked for.
            </p>
          </div>
          {state === "done" ? (
            <div className="flex items-center gap-4 rounded-2xl bg-[#FBF8F4] border border-[#E8DDD3] p-6" data-testid="tour-capture-success">
              <span className="h-11 w-11 rounded-full grid place-items-center text-white shrink-0" style={{ background: accent }}><Check className="h-5 w-5" /></span>
              <div>
                <div className="font-serif text-xl text-[#1F1A17]">You're on the list.</div>
                <div className="text-sm text-[#685F58]">We'll send the tour to {email} as soon as it's live.</div>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3" data-testid="tour-capture-form">
              <label className="relative flex-1">
                <Mail className="absolute left-5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#685F58]" />
                <input
                  type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@family.com" aria-label="Email address"
                  data-testid="tour-capture-email-input"
                  className="w-full h-12 rounded-full border border-[#E8DDD3] bg-[#FBF8F4] pl-12 pr-5 text-sm text-[#1F1A17] outline-none focus:border-[#1F1A17] transition-colors"
                />
              </label>
              <button
                type="submit" disabled={state === "sending"}
                data-testid="tour-capture-submit-btn"
                className="group inline-flex items-center justify-center gap-2 rounded-full bg-[#1F1A17] hover:bg-[#B4532A] disabled:opacity-60 h-12 px-6 text-sm font-medium text-[#FBF8F4] transition-colors duration-300"
              >
                {state === "sending" ? "Sending…" : "Send me the tour"}
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
              {state === "error" && <p className="text-sm text-[#B4532A] sm:absolute sm:mt-14" data-testid="tour-capture-error">Something went wrong — please try again.</p>}
            </form>
          )}
        </div>
      </Reveal>
    </section>
  );
};
